import { bluetoothService } from '../utils/bluetoothService';
import { isDemoMode } from '../utils/demoMode';
import { StreamProcessor } from '../utils/StreamProcessor';
import { SessionManager, sessionManager } from '../utils/SessionManager';
import { MobileStorageService } from './MobileStorageService';
// detectOS unused after SDD 010
import { createLogger } from "../utils/logger";
import type { useRawDataStore } from '../stores/rawDataStore';
import type { useProcessedDataStore } from '../stores/processedDataStore';
import type { useDeviceStore } from '../stores/deviceStore';
import type { useSensorDataStore } from '../stores/sensorDataStore';
import type { useStorageStore } from '../stores/storageStore';
import type { useSystemStore } from '../stores/systemStore';

type RawDataStoreHook = typeof useRawDataStore;
type ProcessedDataStoreHook = typeof useProcessedDataStore;
type DeviceStoreHook = typeof useDeviceStore;
type SensorDataStoreHook = typeof useSensorDataStore;
type StorageStoreHook = typeof useStorageStore;
type SystemStoreHook = typeof useSystemStore;


const logger = createLogger("SystemControl");
/**
 * SystemControlService
 * 
 * 역할: 모든 하위 서비스들의 중앙 컨트롤러 (순수 프로세스 제어)
 * - BluetoothService 관리 (디바이스 연결/해제)
 * - StreamProcessor 관리 (실시간 데이터 처리)
 * - SessionManager 관리 (데이터 저장/로드)
 * - 서비스 간 조정 및 동기화
 * - 상태 관리는 SystemStore에서 담당
 * 
 * Phase 3: 분리된 스토어들과의 데이터 흐름 연결
 */
export class SystemControlService {
  private streamProcessor: StreamProcessor;
  private sessionManager: SessionManager;
  private mobileStorageService: MobileStorageService;
  private isInitialized = false;
  private isConnected = false;
  private isStreaming = false;
  private isRecording = false;
  private currentDeviceId: string | null = null;

  // 모니터링 관련 타이머
  private monitoringInterval: NodeJS.Timeout | null = null;
  private batteryUpdateInterval: NodeJS.Timeout | null = null;
  private samplingRateUpdateInterval: NodeJS.Timeout | null = null;
  
  // 샘플링 레이트 히스토리 (10초 평균 계산용)
  private samplingRateHistory: {
    eeg: number[];
    ppg: number[];
    acc: number[];
  } = {
    eeg: [],
    ppg: [],
    acc: []
  };

  // 스토어 참조 (동적 import로 순환 참조 방지)
  private storeRefs: {
    rawDataStore?: RawDataStoreHook;
    processedDataStore?: ProcessedDataStoreHook;
    deviceStore?: DeviceStoreHook;
    sensorDataStore?: SensorDataStoreHook;
    storageStore?: StorageStoreHook;
    systemStore?: SystemStoreHook;
  } = {};

  constructor() {
    this.streamProcessor = new StreamProcessor();
    this.sessionManager = sessionManager;
    this.mobileStorageService = MobileStorageService.getInstance();
    // SDD 010: IndexedDB 단일 저장소 — S3/FileSystem 제거됨
  }

  /**
   * 시스템 초기화
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    try {
      // 1. Store 참조 로드
      await this.loadStoreReferences();
      
      // 2. 데이터 플로우 설정 (StreamProcessor → SessionManager)
      await this.setupDataFlow();
      
      // 4. 초기화 완료
      this.isInitialized = true;
      
      logger.debug('✅ SystemControlService 초기화 완료');
    } catch (error) {
      logger.error('❌ SystemControlService 초기화 실패:', error);
      throw error;
    }
  }

  /**
   * 스토어 참조 정적 로드 (성능 최적화)
   */
  private async loadStoreReferences(): Promise<void> {
    try {
      // 정적 import로 변경하여 동적 import 중복 문제 해결
      const { useRawDataStore } = await import('../stores/rawDataStore');
      const { useProcessedDataStore } = await import('../stores/processedDataStore');
      const { useDeviceStore } = await import('../stores/deviceStore');
      const { useSensorDataStore } = await import('../stores/sensorDataStore');
      const { useStorageStore } = await import('../stores/storageStore');
      const { useSystemStore } = await import('../stores/systemStore');

      this.storeRefs = {
        rawDataStore: useRawDataStore,
        processedDataStore: useProcessedDataStore,
        deviceStore: useDeviceStore,
        sensorDataStore: useSensorDataStore,
        storageStore: useStorageStore,
        systemStore: useSystemStore
      };
      
      logger.debug('Store references loaded successfully');
    } catch (error) {
      logger.error('Failed to load store references:', error);
      throw error;
    }
  }

  /**
   * S3 클라우드 환경을 위한 데이터 흐름 설정
   * S3CloudStorageService로 데이터를 전송
   */
  /**
   * StreamProcessor 데이터 흐름 설정
   */
  private async setupDataFlow(): Promise<void> {
    if (!this.storeRefs.rawDataStore || !this.storeRefs.processedDataStore) {
      throw new Error('Store references not loaded');
    }

    // 브로드캐스트 버퍼 — Raw 데이터를 100ms 단위로 모아 broadcast 서버로 전송 (spec 027)
    // 서버 미연결 시에는 버퍼만 쌓였다가 flush 타이머에서 즉시 비워진다(전송 안 함).
    const broadcastBuffer: { eegRaw: unknown[]; ppgRaw: unknown[]; accRaw: unknown[] } = {
      eegRaw: [],
      ppgRaw: [],
      accRaw: []
    };

    // SDD 010: SessionManager(IndexedDB)로 직접 데이터 전달
    const callbacks = {
      onEEGData: (data: any[]) => {
        this.sessionManager.addEEGData(data);
        broadcastBuffer.eegRaw.push(...data);
      },
      onPPGData: (data: any[]) => {
        this.sessionManager.addPPGData(data);
        broadcastBuffer.ppgRaw.push(...data);
      },
      onACCData: (data: any[]) => {
        this.sessionManager.addACCData(data);
        broadcastBuffer.accRaw.push(...data);
      },
      onStoreUpdate: (action: string, ...args: unknown[]) => {
        const store = (this.storeRefs.processedDataStore?.getState() ?? {}) as Record<string, (...a: unknown[]) => void>;
        const fn = store[action];
        if (typeof fn === 'function') {
          fn(...args);
        }
      }
    };

    this.streamProcessor.setCallbacks(callbacks);

    // 브로드캐스트 플러시 타이머 (100ms마다) — 연결된 경우에만 Raw 데이터 publish (spec 027)
    setInterval(async () => {
      const { useBroadcastStore } = await import('../stores/broadcastStore');
      const broadcastState = useBroadcastStore.getState();

      const hasData = broadcastBuffer.eegRaw.length > 0 ||
                      broadcastBuffer.ppgRaw.length > 0 ||
                      broadcastBuffer.accRaw.length > 0;
      if (!hasData) return;

      // 실측 생체 데이터 외부 전송 동의 게이트 (WS-C)
      // 동의는 StreamProcess 연결 시 사용자가 명시적으로 부여하며 localStorage에 보관.
      // 동의가 없으면 연결 상태와 무관하게 publish하지 않는다(개인정보처리방침 §3).
      const hasConsent = typeof localStorage !== 'undefined' &&
                         localStorage.getItem('broadcast-consent') === 'granted';

      // 데모 모드의 합성 데이터는 외부로 전송하지 않는다.
      if (!isDemoMode() && hasConsent && broadcastState.isConnected && broadcastState.client) {
        try {
          await broadcastState.publish({
            version: '1.0',
            eegRaw: broadcastBuffer.eegRaw.length > 0 ? [...broadcastBuffer.eegRaw] as BroadcastPayload['eegRaw'] : undefined,
            ppgRaw: broadcastBuffer.ppgRaw.length > 0 ? [...broadcastBuffer.ppgRaw] as BroadcastPayload['ppgRaw'] : undefined,
            accRaw: broadcastBuffer.accRaw.length > 0 ? [...broadcastBuffer.accRaw] as BroadcastPayload['accRaw'] : undefined
          });
        } catch (error) {
          logger.error('Broadcast publish error:', error);
        }
      }

      // 전송 여부와 무관하게 버퍼 비움 (미연결 시 무한 누적 방지)
      broadcastBuffer.eegRaw = [];
      broadcastBuffer.ppgRaw = [];
      broadcastBuffer.accRaw = [];
    }, 100);

    logger.debug('✅ 데이터 플로우 설정 완료 (StreamProcessor → SessionManager + Broadcast)');
  }

  /**
   * 디바이스 스캔
   */
  async scanDevices(): Promise<any[]> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      const devices = await bluetoothService.scan();
      return devices;
    } catch (error) {
      logger.error('Device scan failed:', error);
      throw error;
    }
  }

  /**
   * 디바이스 연결
   */
  async connectDevice(deviceId: string): Promise<void> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    if (this.isConnected) {
      throw new Error('다른 디바이스가 이미 연결되어 있습니다.');
    }

    try {
      // 1. BluetoothService에 콜백 설정 (배터리 정보만)
      bluetoothService.setSystemCallbacks({
        onBatteryUpdate: (level: number, voltage?: number) => {
          if (this.storeRefs.deviceStore) {
            this.storeRefs.deviceStore.getState().updateBatteryInfo(level, voltage);
          }
        }
      });

      // 2. Bluetooth 연결
      await bluetoothService.connect(deviceId);
      
      // 3. 연결된 디바이스 정보 생성
      const deviceInfo = {
        id: deviceId,
        name: bluetoothService.getDeviceName() || 'Unknown Device',
        batteryLevel: bluetoothService.getBatteryLevel() || 0
      };
      
      // 4. DeviceStore에 연결된 디바이스 상태 업데이트
      if (this.storeRefs.deviceStore) {
        this.storeRefs.deviceStore.getState().startDeviceConnection(deviceInfo);
      }
      
      // 5. StreamProcessor에 BluetoothService 연결
      this.streamProcessor.setBluetoothService(bluetoothService);
      
      // 6. 연결 상태 업데이트 (내부 상태만)
      this.isConnected = true;
      this.currentDeviceId = deviceId;
      
      // 7. ProcessedDataStore 연결 상태 업데이트
      this.storeRefs.processedDataStore?.getState().setConnectionState(true);
      
      // 8. 모니터링 시작
      this.startConnectionMonitoring();
      
      logger.debug(`Device ${deviceId} connected successfully`);
      
      // 9. 🔧 자동 스트리밍 시작 (디바이스 연결 후 바로 데이터 수집 시작)
      try {
        await this.startStreaming();
        logger.debug('🔧 Auto-streaming started after device connection');
      } catch (streamError) {
        logger.error('🔧 Auto-streaming failed:', streamError);
        // 스트리밍 실패해도 연결은 유지
      }
    } catch (error) {
      logger.error('Device connection failed:', error);
      this.isConnected = false;
      this.currentDeviceId = null;
      throw error;
    }
  }

  /**
   * 등록된 디바이스 관리
   */
  async registerDevice(device: any, nickname?: string): Promise<void> {
    try {
      this.storeRefs.deviceStore?.getState().registerDevice(device, nickname);
      logger.debug(`Device ${device.name} registered successfully`);
    } catch (error) {
      logger.error('Device registration failed:', error);
      throw error;
    }
  }

  async unregisterDevice(deviceId: string): Promise<void> {
    try {
      this.storeRefs.deviceStore?.getState().unregisterDevice(deviceId);
      logger.debug(`Device ${deviceId} unregistered successfully`);
    } catch (error) {
      logger.error('Device unregistration failed:', error);
      throw error;
    }
  }

  /**
   * 디바이스 모니터링 정보 조회 (BluetoothService에서 직접 조회)
   */
  getDeviceMonitoringInfo(): any {
    if (!this.isConnected || !this.currentDeviceId) {
      return null;
    }

    try {
      return {
        deviceId: bluetoothService.getDeviceId(),
        deviceName: bluetoothService.getDeviceName(),
        batteryLevel: bluetoothService.getBatteryLevel(),
        connectionDuration: bluetoothService.getConnectionDuration(),
        connectionStartTime: bluetoothService.getConnectionStartTime(),
        samplingRates: bluetoothService.getCurrentSamplingRates()
      };
    } catch (error) {
      logger.error('Failed to get device monitoring info:', error);
      return null;
    }
  }

  /**
   * 실시간 샘플링 레이트 조회
   */
  getCurrentSamplingRates(): { eeg: number; ppg: number; acc: number } | null {
    try {
      return bluetoothService.getCurrentSamplingRates();
    } catch (error) {
      logger.error('Failed to get sampling rates:', error);
      return null;
    }
  }

  /**
   * 배터리 정보 조회
   */
  async getBatteryInfo(): Promise<{ level: number; decreaseRate: number; timeRemaining: string } | null> {
    const deviceStore = this.storeRefs.deviceStore?.getState();
    if (!deviceStore || !deviceStore.connectedDevice) {
      return null;
    }

    const battery = deviceStore.connectedDevice.battery;
    return {
      level: battery.level,
      decreaseRate: battery.decreaseRate,
      timeRemaining: deviceStore.batteryTimeRemainingFormatted
    };
  }

  /**
   * 디바이스 연결 해제
   */
  async disconnectDevice(): Promise<void> {
    logger.debug('🔧 SystemControlService 연결 해제 시작...');
    
    if (!this.isConnected) {
      logger.debug('⚠️ 이미 연결 해제됨');
      return;
    }

    try {
      // 1. 모니터링 중지
      logger.debug('🔧 모니터링 중지...');
      this.stopConnectionMonitoring();

      // 2. 레코딩 중지 (레코딩 중인 경우만)
      if (this.isRecording) {
        logger.debug('🔧 레코딩 중지...');
        try {
          await this.stopRecording();
        } catch (recordingError) {
          logger.error('⚠️ 레코딩 중지 실패 (연결 해제 계속 진행):', recordingError);
        }
      }

      // 3. 스트리밍 중지
      if (this.isStreaming) {
        logger.debug('🔧 스트리밍 중지...');
        try {
          await this.stopStreaming();
        } catch (streamingError) {
          logger.error('⚠️ 스트리밍 중지 실패 (연결 해제 계속 진행):', streamingError);
        }
      }

      // 4. StreamProcessor 정리
      logger.debug('🔧 StreamProcessor 정리...');
      this.streamProcessor.cleanup();

      // 5. Bluetooth 연결 해제
      logger.debug('🔧 Bluetooth 연결 해제...');
      await bluetoothService.disconnect();
      
      // 6. 추가 대기 시간 (완전한 연결 해제 보장)
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // 7. 연결 상태 업데이트 (내부 상태만)
      this.isConnected = false;
      this.currentDeviceId = null;
      this.isStreaming = false;
      this.isRecording = false;
      
      // 8. ProcessedDataStore 연결 상태 업데이트
      this.storeRefs.processedDataStore?.getState().setConnectionState(false);
      
      // 9. RawDataStore 정리
      this.storeRefs.rawDataStore?.getState().reset();
      
      // 10. 샘플링 레이트 히스토리 초기화
      this.samplingRateHistory = {
        eeg: [],
        ppg: [],
        acc: []
      };
      
      logger.debug('✅ SystemControlService 연결 해제 완료');
    } catch (error) {
      logger.error('❌ SystemControlService 연결 해제 실패:', error);
      
      // 에러가 발생해도 상태는 초기화
      this.isConnected = false;
      this.currentDeviceId = null;
      this.isStreaming = false;
      this.isRecording = false;
      
      // 강제로 Bluetooth 캐시 정리
      try {
        bluetoothService.clearDeviceCache();
      } catch (cleanupError) {
        logger.error('❌ 캐시 정리 실패:', cleanupError);
      }
      
      throw error;
    }
  }

  /**
   * 스트리밍 시작
   */
  async startStreaming(): Promise<void> {
    if (!this.isConnected) {
      throw new Error('디바이스가 연결되지 않았습니다.');
    }

    if (this.isStreaming) {
      logger.debug('Streaming already active');
      return;
    }

    try {
      // BluetoothService 상태 확인
      if (!bluetoothService.isConnected()) {
        throw new Error('BluetoothService가 연결되지 않았습니다.');
      }

      // StreamProcessor에 BluetoothService가 설정되어 있는지 확인
      if (!this.streamProcessor) {
        throw new Error('StreamProcessor가 초기화되지 않았습니다.');
      }

      logger.debug('🔧 스트리밍 시작 전 상태 확인:', {
        isConnected: this.isConnected,
        bluetoothConnected: bluetoothService.isConnected(),
        streamProcessorExists: !!this.streamProcessor,
        currentDeviceId: this.currentDeviceId
      });

      // StreamProcessor 시작 (실시간 데이터 처리)
      await this.streamProcessor.start();
      
      // 스트리밍 상태 업데이트 (내부 상태만)
      this.isStreaming = true;
      
      logger.debug('Streaming started successfully');
    } catch (error) {
      logger.error('Streaming start failed:', error);
      this.isStreaming = false;
      throw error;
    }
  }

  /**
   * 스트리밍 중지
   */
  async stopStreaming(): Promise<void> {
    if (!this.isStreaming) {
      logger.debug('Streaming not active');
      return;
    }

    try {
      // StreamProcessor 정리
      this.streamProcessor.cleanup();
      
      // 스트리밍 상태 업데이트 (내부 상태만)
      this.isStreaming = false;
      
      logger.debug('Streaming stopped successfully');
    } catch (error) {
      logger.error('Streaming stop failed:', error);
      throw error;
    }
  }

  /**
   * 레코딩 시작
   */
  async startRecording(sessionName?: string, customConfig?: any): Promise<string> {
    if (!this.isConnected) {
      throw new Error('디바이스가 연결되지 않았습니다.');
    }

    // 이전 레코딩 상태 정리
    if (this.isRecording) {
      try { await this.stopRecording(); } catch (e) {}
    }

    try {
      // 1. 스트리밍 시작
      if (!this.isStreaming) {
        await this.startStreaming();
      }

      // 2. 디바이스 정보
      const deviceInfo = await this.getConnectedDeviceInfo();
      if (!deviceInfo) throw new Error('디바이스 정보를 찾을 수 없습니다.');

      // 3. IndexedDB 기반 세션 시작
      const sessionId = this.sessionManager.startSession(
        deviceInfo.name || 'Unknown Device',
        deviceInfo.id || 'unknown',
        sessionName
      );

      // 4. 상태 업데이트
      this.isRecording = true;
      if (this.storeRefs.storageStore) {
        this.storeRefs.storageStore.getState().setCurrentSession(sessionId);
        this.storeRefs.storageStore.getState().setIsRecording(true);
      }
      if (this.storeRefs.sensorDataStore) {
        await this.storeRefs.sensorDataStore.getState().startRecording(sessionId);
      }

      logger.debug('레코딩 시작 완료:', sessionId);
      return sessionId;
    } catch (error) {
      logger.error('레코딩 시작 실패:', error);
      throw error;
    }
  }

  async stopRecording(): Promise<void> {
    if (!this.isRecording) return;

    try {
      // SessionManager 세션 종료 (IndexedDB)
      this.sessionManager.endSession();

      // 상태 정리
      this.isRecording = false;
      if (this.storeRefs.storageStore) {
        this.storeRefs.storageStore.getState().setCurrentSession(null);
        this.storeRefs.storageStore.getState().setIsRecording(false);
      }
      if (this.storeRefs.sensorDataStore) {
        this.storeRefs.sensorDataStore.getState().stopRecording();
      }
      logger.debug('레코딩 중지 완료 (블루투스 연결 유지)');
    } catch (error) {
      logger.error('레코딩 중지 실패:', error);
      this.isRecording = false;
      throw error;
    }
  }

  /**
   * 현재 상태 조회
   */
  /**
   * 시스템 메트릭 조회 (Python SDK 서버 연동)
   */
  async getSystemMetrics(): Promise<{ cpuUsage: number; memoryUsage: number } | null> {
    // SDD 010: Python SDK 서버 없음 — null 반환
    return null;
  }

  getStatus(): {
    isInitialized: boolean;
    isConnected: boolean;
    isStreaming: boolean;
    isRecording: boolean;
    currentDeviceId: string | null;
  } {
    return {
      isInitialized: this.isInitialized,
      isConnected: this.isConnected,
      isStreaming: this.isStreaming,
      isRecording: this.isRecording,
      currentDeviceId: this.currentDeviceId
    };
  }

  /**
   * 연결된 디바이스 정보 조회
   */
  async getConnectedDeviceInfo(): Promise<{ id: string; name: string; batteryLevel: number } | null> {
    if (!this.isConnected || !this.currentDeviceId) {
      logger.warn('🔧 연결된 디바이스가 없음:', {
        isConnected: this.isConnected,
        currentDeviceId: this.currentDeviceId
      });
      return null;
    }

    try {
      // BluetoothService에서 디바이스 정보 조회
      const deviceId = bluetoothService.getDeviceId();
      const deviceName = bluetoothService.getDeviceName();
      const batteryLevel = await bluetoothService.getBatteryLevel();

      logger.debug('🔧 BluetoothService에서 가져온 디바이스 정보:', {
        deviceId,
        deviceName,
        batteryLevel,
        currentDeviceId: this.currentDeviceId
      });

      // DeviceStore에서 디바이스 정보 조회 (보조)
      const deviceStore = this.storeRefs.deviceStore?.getState();
      const connectedDevice = deviceStore?.connectedDevice;

      logger.debug('🔧 DeviceStore에서 가져온 디바이스 정보:', {
        hasConnectedDevice: !!connectedDevice,
        deviceId: connectedDevice?.id,
        deviceName: connectedDevice?.name,
        batteryLevel: connectedDevice?.battery?.level
      });

      return {
        id: deviceId || this.currentDeviceId,
        name: deviceName || connectedDevice?.name || 'Unknown Device',
        batteryLevel: batteryLevel || connectedDevice?.battery?.level || 0
      };
    } catch (error) {
      logger.error('🔧 디바이스 정보 조회 실패:', error);
      
      // 기본값 반환
      return {
        id: this.currentDeviceId,
        name: 'Unknown Device',
        batteryLevel: 0
      };
    }
  }

  /**
   * 세션 목록 조회
   */
  async getSessionList(): Promise<any[]> {
    // SessionManager 또는 SensorDataStore에서 세션 목록 조회
    return this.storeRefs.sensorDataStore?.getState().getAllSessions() || [];
  }

  /**
   * 세션 로드
   */
  async loadSession(sessionId: string): Promise<any> {
    return this.storeRefs.sensorDataStore?.getState().getSessionData(sessionId) || null;
  }

  /**
   * 세션 삭제
   */
  async deleteSession(sessionId: string): Promise<void> {
    // 실제 삭제 로직은 추후 구현
    logger.debug(`Session ${sessionId} deletion requested`);
  }

  /**
   * 연결 모니터링 시작 (배터리 폴링 제거됨)
   */
  private startConnectionMonitoring(): void {
    if (this.monitoringInterval) {
      return; // 이미 모니터링 중
    }

    // 1초마다 연결 상태 및 샘플링 레이트 확인
    this.monitoringInterval = setInterval(() => {
      // 연결 상태 확인
      if (!bluetoothService.isConnected()) {
        logger.warn('Device connection lost detected');
        this.handleConnectionLoss();
        return;
      }

      // 샘플링 레이트 업데이트
      try {
        const samplingRates = bluetoothService.getCurrentSamplingRates();
        if (this.storeRefs.deviceStore && samplingRates) {
          this.storeRefs.deviceStore.getState().updateSamplingRates(samplingRates);
        }
      } catch (error) {
        logger.error('Failed to update sampling rates:', error);
      }
    }, 1000); // 1초마다 업데이트

    logger.debug('Connection monitoring started');
  }

  /**
   * 연결 끊김 처리
   */
  private handleConnectionLoss(): void {
    this.isConnected = false;
    this.currentDeviceId = null;
    this.stopConnectionMonitoring();
    
    // DeviceStore에 연결 해제 알림
    if (this.storeRefs.deviceStore) {
      this.storeRefs.deviceStore.getState().disconnectDevice();
    }
  }

  /**
   * 연결 모니터링 중지
   */
  private stopConnectionMonitoring(): void {
    if (this.monitoringInterval) {
      clearInterval(this.monitoringInterval);
      this.monitoringInterval = null;
    }

    if (this.batteryUpdateInterval) {
      clearInterval(this.batteryUpdateInterval);
      this.batteryUpdateInterval = null;
    }

    if (this.samplingRateUpdateInterval) {
      clearInterval(this.samplingRateUpdateInterval);
      this.samplingRateUpdateInterval = null;
    }

    logger.debug('Connection monitoring stopped');
  }

  // 배터리 정보 업데이트, 샘플링 레이트 업데이트, 연결 지속 시간 업데이트는
  // 이제 BluetoothService에서 콜백으로 처리됩니다.

  /**
   * 시스템 정리
   */
  async cleanup(): Promise<void> {
    try {
      // 1. 모니터링 중지
      this.stopConnectionMonitoring();

      // 2. 레코딩 중지
      if (this.isRecording) {
        await this.stopRecording();
      }

      // 3. 스트리밍 중지
      if (this.isStreaming) {
        await this.stopStreaming();
      }

      // 4. 디바이스 연결 해제
      if (this.isConnected) {
        await this.disconnectDevice();
      }

      // 5. 스토어 정리
      this.storeRefs.rawDataStore?.getState().reset();
      this.storeRefs.processedDataStore?.getState().reset();
      this.storeRefs.deviceStore?.getState().reset();
      this.storeRefs.sensorDataStore?.getState().reset();

      // 6. 샘플링 레이트 히스토리 초기화
      this.samplingRateHistory = {
        eeg: [],
        ppg: [],
        acc: []
      };

      // 7. 초기화 상태 리셋
      this.isInitialized = false;
      this.storeRefs = {};
      
      logger.debug('SystemControlService cleanup completed');
    } catch (error) {
      logger.error('SystemControlService cleanup failed:', error);
      throw error;
    }
  }
}

// 싱글톤 인스턴스 생성 및 내보내기
export const systemControlService = new SystemControlService(); 